import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@themesberg/react-bootstrap';
import { FaLock, FaUnlock, FaExclamationTriangle } from 'react-icons/fa';
import { useCollaborationState } from '../../hooks/useCollaboration';
import { takeOverScope } from '../../collaboration/collaborationSocket';
import {
  discardChanges,
  holderName,
  isDenied,
  isDirty,
  isHeldByOther,
  markDirty,
  remoteSaveCount,
} from '../../collaboration/store';
import './collaboration.css';

/**
 * Envuelve un formulario del peritaje para que varios peritos puedan trabajar
 * sobre el mismo peritaje sin pisarse.
 *
 * - En cuanto el formulario tiene cambios sin guardar pide el lock del scope.
 * - Si otro perito lo tiene tomado, se muestra en solo lectura con su nombre.
 * - Si el servidor rechaza el guardado por versión obsoleta (409), avisa en vez
 *   de sobreescribir.
 * - Cuando otro perito guarda este mismo formulario, se recarga solo siempre
 *   que este no tenga cambios locales.
 *
 * Uso: <FormScope scope="fluids:F" label="Fluidos">{children}</FormScope>
 */
export default function FormScope({ scope, label, children }) {
  const collab = useCollaborationState();
  const [reloadKey, setReloadKey] = useState(0);
  const lastRemoteSave = useRef(remoteSaveCount(scope));

  const heldByOther = isHeldByOther(scope);
  const holder = holderName(scope);
  const conflict = collab.conflicts[scope];

  // Cambios del usuario dentro del formulario → el formulario queda "sucio" y
  // por tanto este scope queda tomado por mí.
  const handleUserEdit = () => {
    if (heldByOther) return;
    markDirty(scope);
  };

  // Descartar los cambios locales y volver a leer el formulario del servidor.
  const reload = useCallback(() => {
    discardChanges(scope);
    setReloadKey((key) => key + 1);
  }, [scope]);

  // Otro perito guardó este formulario: lo recargamos si no tengo cambios.
  useEffect(() => {
    const current = remoteSaveCount(scope);
    if (current === lastRemoteSave.current) return;
    lastRemoteSave.current = current;
    if (!isDirty(scope)) reload();
  }, [collab, scope, reload]);

  return (
    <div
      className={`collab-form${heldByOther ? ' collab-form--locked' : ''}`}
      onChange={handleUserEdit}
      onInput={handleUserEdit}
    >
      {heldByOther && (
        <div className="collab-banner collab-banner--lock">
          <FaLock className="me-2" />
          <span>
            <strong>{holder || 'Otro perito'}</strong> está editando {label || 'este formulario'}.
            No podés guardar hasta que termine.
          </span>
          <Button
            size="sm"
            variant="outline-light"
            className="ms-auto"
            onClick={() => takeOverScope(scope)}
          >
            <FaUnlock className="me-1" /> Tomar control
          </Button>
        </div>
      )}

      {!heldByOther && isDenied(scope) && (
        <div className="collab-banner collab-banner--denied">
          <FaLock className="me-2" />
          <span>
            {holder ? `${holder} tiene este formulario` : 'Perdiste el control de este formulario'}.
            Recargá para ver la versión más reciente.
          </span>
        </div>
      )}

      {conflict && (
        <div className="collab-banner collab-banner--conflict">
          <FaExclamationTriangle className="me-2" />
          <span>{conflict.message}</span>
          <Button
            size="sm"
            variant="outline-light"
            className="ms-auto"
            onClick={reload}
          >
            Recargar formulario
          </Button>
        </div>
      )}

      <div key={reloadKey}>{children}</div>
    </div>
  );
}